import * as React from "react";

export interface Size {
  width: number;
  height: number;
}

interface WithSizeProp {
  size: Size;
}

export function withSize() {
  return function <P extends WithSizeProp>(
    WrappedComponent: React.ComponentType<P>,
  ): React.FC<Omit<P, "size">> {
    const WithSize: React.FC<Omit<P, "size">> = (props) => {
      const ref = React.useRef<HTMLDivElement>(null);
      // Match react-sizeme's two-phase behaviour: render at 0 first, then
      // re-render once the real width has been measured.
      const [size, setSize] = React.useState<Size>({ width: 0, height: 0 });

      React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const { width, height } = entry.contentRect;
            setSize((prev) =>
              prev.width === width && prev.height === height
                ? prev
                : { width, height },
            );
          }
        });

        observer.observe(el);
        return () => observer.disconnect();
      }, []);

      return (
        <div ref={ref} style={{ width: "100%" }}>
          <WrappedComponent {...(props as P)} size={size} />
        </div>
      );
    };

    WithSize.displayName = `SizeMe(${
      WrappedComponent.displayName || WrappedComponent.name || "Component"
    })`;

    return WithSize;
  };
}
